'use client';

import React, { useState, useRef, useEffect, forwardRef } from 'react';
import classnames from 'classnames';

import Typography from '../Typography/Typography';
import Iconography from '../Iconography/Iconography';
import Box from '../Box/Box';

import { dataLayerHandler } from '../../../utils/analytics';
import { COUNTRY_CODES } from './PhoneInput.constant';
import {
  getExampleNumber,
  AsYouType,
  validatePhoneNumberLength,
  parsePhoneNumberWithError,
} from 'libphonenumber-js';
import type { CountryCode as LibCountryCode } from 'libphonenumber-js';
import examples from 'libphonenumber-js/mobile/examples';

import styles from './PhoneInput.module.scss';

// Type definitions
type Theme = 'dark' | 'light';
type Size = 'small' | 'medium' | 'large';
type Direction = 'ltr' | 'rtl';
export type CountryCode =
  | 'AC'
  | 'AD'
  | 'AE'
  | 'AF'
  | 'AG'
  | 'AI'
  | 'AL'
  | 'AM'
  | 'AO'
  | 'AR'
  | 'AS'
  | 'AT'
  | 'AU'
  | 'AW'
  | 'AX'
  | 'AZ'
  | 'BA'
  | 'BB'
  | 'BD'
  | 'BE'
  | 'BF'
  | 'BG'
  | 'BH'
  | 'BI'
  | 'BJ'
  | 'BL'
  | 'BM'
  | 'BN'
  | 'BO'
  | 'BQ'
  | 'BR'
  | 'BS'
  | 'BT'
  | 'BW'
  | 'BY'
  | 'BZ'
  | 'CA'
  | 'CC'
  | 'CD'
  | 'CF'
  | 'CG'
  | 'CH'
  | 'CI'
  | 'CK'
  | 'CL'
  | 'CM'
  | 'CN'
  | 'CO'
  | 'CR'
  | 'CU'
  | 'CV'
  | 'CW'
  | 'CX'
  | 'CY'
  | 'CZ'
  | 'DE'
  | 'DJ'
  | 'DK'
  | 'DM'
  | 'DO'
  | 'DZ'
  | 'EC'
  | 'EE'
  | 'EG'
  | 'EH'
  | 'ER'
  | 'ES'
  | 'ET'
  | 'FI'
  | 'FJ'
  | 'FK'
  | 'FM'
  | 'FO'
  | 'FR'
  | 'GA'
  | 'GB'
  | 'GD'
  | 'GE'
  | 'GF'
  | 'GG'
  | 'GH'
  | 'GI'
  | 'GL'
  | 'GM'
  | 'GN'
  | 'GP'
  | 'GQ'
  | 'GR'
  | 'GT'
  | 'GU'
  | 'GW'
  | 'GY'
  | 'HK'
  | 'HN'
  | 'HR'
  | 'HT'
  | 'HU'
  | 'ID'
  | 'IE'
  | 'IL'
  | 'IM'
  | 'IN'
  | 'IO'
  | 'IQ'
  | 'IR'
  | 'IS'
  | 'IT'
  | 'JE'
  | 'JM'
  | 'JO'
  | 'JP'
  | 'KE'
  | 'KG'
  | 'KH'
  | 'KI'
  | 'KM'
  | 'KN'
  | 'KP'
  | 'KR'
  | 'KW'
  | 'KY'
  | 'KZ'
  | 'LA'
  | 'LB'
  | 'LC'
  | 'LI'
  | 'LK'
  | 'LR'
  | 'LS'
  | 'LT'
  | 'LU'
  | 'LV'
  | 'LY'
  | 'MA'
  | 'MC'
  | 'MD'
  | 'ME'
  | 'MF'
  | 'MG'
  | 'MH'
  | 'MK'
  | 'ML'
  | 'MM'
  | 'MN'
  | 'MO'
  | 'MP'
  | 'MQ'
  | 'MR'
  | 'MS'
  | 'MT'
  | 'MU'
  | 'MV'
  | 'MW'
  | 'MX'
  | 'MY'
  | 'MZ'
  | 'NA'
  | 'NC'
  | 'NE'
  | 'NF'
  | 'NG'
  | 'NI'
  | 'NL'
  | 'NO'
  | 'NP'
  | 'NR'
  | 'NU'
  | 'NZ'
  | 'OM'
  | 'PA'
  | 'PE'
  | 'PF'
  | 'PG'
  | 'PH'
  | 'PK'
  | 'PL'
  | 'PM'
  | 'PR'
  | 'PS'
  | 'PT'
  | 'PW'
  | 'PY'
  | 'QA'
  | 'RE'
  | 'RO'
  | 'RS'
  | 'RU'
  | 'RW'
  | 'SA'
  | 'SB'
  | 'SC'
  | 'SD'
  | 'SE'
  | 'SG'
  | 'SH'
  | 'SI'
  | 'SJ'
  | 'SK'
  | 'SL'
  | 'SM'
  | 'SN'
  | 'SO'
  | 'SR'
  | 'SS'
  | 'ST'
  | 'SV'
  | 'SX'
  | 'SY'
  | 'SZ'
  | 'TA'
  | 'TC'
  | 'TD'
  | 'TG'
  | 'TH'
  | 'TJ'
  | 'TK'
  | 'TL'
  | 'TM'
  | 'TN'
  | 'TO'
  | 'TR'
  | 'TT'
  | 'TV'
  | 'TW'
  | 'TZ'
  | 'UA'
  | 'UG'
  | 'US'
  | 'UY'
  | 'UZ'
  | 'VA'
  | 'VC'
  | 'VE'
  | 'VG'
  | 'VI'
  | 'VN'
  | 'VU'
  | 'WF'
  | 'WS'
  | 'XK'
  | 'YE'
  | 'YT'
  | 'ZA'
  | 'ZM'
  | 'ZW';
type ValidationType = 'success' | 'warning' | 'error';

interface ValidationResponse {
  validationType?: ValidationType;
  message?: string;
}

interface CountryOption {
  countryCode: string;
  label: string;
  phoneCode: string;
}

export interface PhoneInputProps {
  theme?: Theme;
  size?: Size;
  name?: string;
  register?: (name: string) => Record<string, unknown>;
  label?: string;
  helperText?: string;
  selectedOption?: CountryCode;
  /**
   * Locale string (e.g., 'en-US', 'de-DE', 'nl-NL') to auto-select country code.
   * Takes precedence over selectedOption when provided.
   */
  locale?: string;
  direction?: Direction;
  /**
   * Validation response object containing error/warning/success state and message.
   */
  validationResponse?: ValidationResponse;
  /**
   * Whether to show the validation message area.
   * @default true
   */
  showValidation?: boolean;
  gaComponent?: string;
  required?: boolean;
  disabled?: boolean;
  inline?: boolean;
  /**
   * Initial value in E.164 format or national format
   */
  value?: string;
}

/**
 * Maps locale string to country code
 * @param locale - Locale string (e.g., 'en-US', 'de-DE', 'nl-NL')
 * @returns Country code or undefined if no match
 */
const getCountryCodeFromLocale = (locale?: string): CountryCode | undefined => {
  if (!locale) return undefined;

  // Extract country code from locale (e.g., 'de-DE' -> 'DE', 'nl-NL' -> 'NL')
  const parts = locale.split(/[-_]/);
  const countryPart = parts[1]?.toUpperCase() || parts[0]?.toUpperCase();

  // Check if country code exists in COUNTRY_CODES
  if (countryPart && COUNTRY_CODES.some((c) => c.countryCode === countryPart)) {
    return countryPart as CountryCode;
  }

  // Fallback mappings for language-only locales
  const languageToCountry: Record<string, CountryCode> = {
    en: 'US',
    de: 'DE',
    nl: 'NL',
    ar: 'AE',
    no: 'NO',
    nb: 'NO', // Norwegian Bokmål
    nn: 'NO', // Norwegian Nynorsk
    fr: 'CH', // French -> Switzerland as default
    it: 'CH', // Italian -> Switzerland as default
  };

  const languagePart = parts[0]?.toLowerCase();
  return languagePart ? languageToCountry[languagePart] : undefined;
};

const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  (
    {
      theme = 'dark',
      size = 'medium',
      name,
      register,
      label,
      helperText,
      selectedOption = 'US',
      locale,
      direction = 'ltr',
      validationResponse,
      showValidation = true,
      gaComponent = 'phoneInput',
      required = false,
      disabled,
      inline = false,
      value,
    },
    ref
  ) => {
    const [optionSelected, setOptionSelected] = useState<CountryOption | undefined>();
    const [placeholder, setPlaceholder] = useState<string>('');
    const [phoneNumber, setPhoneNumber] = useState<string>('');
    const [e164Value, setE164Value] = useState<string>('');
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [previousCountryCode, setPreviousCountryCode] = useState<string | undefined>();
    const [focusedIndex, setFocusedIndex] = useState<number>(-1);
    const [searchBuffer, setSearchBuffer] = useState<string>('');
    const dropdownButtonRef = useRef<HTMLDivElement>(null);
    const dropdownMenuRef = useRef<HTMLUListElement>(null);
    const hiddenInputRef = useRef<HTMLInputElement>(null);
    const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const optionRefs = useRef<(HTMLLIElement | null)[]>([]);

    // Get register props and extract onChange to call it manually
    const registerProps = register ? register(name || 'phone') : {};
    const registerOnChange = registerProps.onChange as
      | ((e: React.ChangeEvent<HTMLInputElement>) => void)
      | undefined;

    const handleMenuItemClick = (option: CountryOption) => {
      setIsMenuOpen(false);
      setFocusedIndex(-1);
      setOptionSelected(option);
      dataLayerHandler({
        interactionType: 'dropdown',
        gaComponent,
        label,
      });
    };

    const handleButtonClick = () => {
      if (!disabled) {
        setIsMenuOpen((prevState) => !prevState);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (disabled) return;

      switch (e.key) {
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (isMenuOpen && focusedIndex >= 0) {
            handleMenuItemClick(COUNTRY_CODES[focusedIndex]);
          } else {
            setIsMenuOpen((prevState) => !prevState);
          }
          break;
        case 'Escape':
          setIsMenuOpen(false);
          setFocusedIndex(-1);
          break;
        case 'ArrowDown':
          e.preventDefault();
          if (!isMenuOpen) {
            setIsMenuOpen(true);
            setFocusedIndex(0);
          } else {
            setFocusedIndex((prev) => (prev < COUNTRY_CODES.length - 1 ? prev + 1 : 0));
          }
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (isMenuOpen) {
            setFocusedIndex((prev) => (prev > 0 ? prev - 1 : COUNTRY_CODES.length - 1));
          }
          break;
        case 'Tab':
          if (isMenuOpen) {
            setIsMenuOpen(false);
            setFocusedIndex(-1);
          }
          break;
        default:
          // Type-ahead search: match country name or phone code
          if (e.key.length === 1 && /[a-zA-Z0-9+]/.test(e.key)) {
            e.preventDefault();
            const newBuffer = searchBuffer + e.key.toLowerCase();
            setSearchBuffer(newBuffer);

            // Clear previous timeout
            if (searchTimeoutRef.current) {
              clearTimeout(searchTimeoutRef.current);
            }

            // Clear buffer after 500ms of inactivity
            searchTimeoutRef.current = setTimeout(() => {
              setSearchBuffer('');
            }, 500);

            // Find matching option
            const matchIndex = COUNTRY_CODES.findIndex(
              (option) =>
                option.label.toLowerCase().startsWith(newBuffer) ||
                option.phoneCode.startsWith(newBuffer) ||
                option.countryCode.toLowerCase().startsWith(newBuffer)
            );

            if (matchIndex >= 0) {
              if (!isMenuOpen) {
                setIsMenuOpen(true);
              }
              setFocusedIndex(matchIndex);
            }
          }
          break;
      }
    };

    const handleDocumentClick = (e: MouseEvent) => {
      if (
        !dropdownButtonRef?.current?.contains(e.target as Node) &&
        !dropdownMenuRef?.current?.contains(e.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    };

    /**
     * Converts a phone number to E.164 format
     * @param phoneValue - The phone number to convert
     * @param countryCode - The country code
     * @returns The E.164 formatted number or empty string if invalid
     */
    const toE164Format = (phoneValue: string, countryCode: string): string => {
      if (!phoneValue || !countryCode) return '';
      try {
        const parsed = parsePhoneNumberWithError(phoneValue, countryCode as LibCountryCode);
        if (parsed) {
          return parsed.format('E.164');
        }
      } catch {
        // Invalid phone number, return empty string
      }
      return '';
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const phoneFormat = new AsYouType(optionSelected?.countryCode as CountryCode);
      const inputValue = e.target.value;
      const validLength = validatePhoneNumberLength(
        inputValue,
        optionSelected?.countryCode as CountryCode
      );
      const nativeEvent = e.nativeEvent as InputEvent;

      let newPhoneNumber = inputValue;

      if (nativeEvent.inputType === 'deleteContentBackward') {
        newPhoneNumber = inputValue;
      } else if (validLength !== 'TOO_LONG') {
        newPhoneNumber = phoneFormat.input(inputValue);
      } else {
        // Don't update if too long
        return;
      }

      setPhoneNumber(newPhoneNumber);

      // Update E.164 value for form submission
      if (optionSelected?.countryCode) {
        const newE164Value = toE164Format(newPhoneNumber, optionSelected.countryCode);
        setE164Value(newE164Value);
      }
    };

    // Initialize phone number from value prop
    useEffect(() => {
      if (value && optionSelected?.countryCode) {
        try {
          const parsed = parsePhoneNumberWithError(
            value,
            optionSelected.countryCode as LibCountryCode
          );
          if (parsed) {
            setPhoneNumber(parsed.formatNational());
            setE164Value(parsed.format('E.164'));
          }
        } catch {
          // If parsing fails, use value directly
          setPhoneNumber(value);
          setE164Value(value);
        }
      }
    }, [value, optionSelected?.countryCode]);

    // When E.164 value changes, notify react-hook-form by calling register's onChange
    useEffect(() => {
      if (hiddenInputRef.current && registerOnChange) {
        // Create a synthetic event with the E.164 value
        const syntheticEvent = {
          target: {
            name: name || 'phone',
            value: e164Value,
          },
        } as React.ChangeEvent<HTMLInputElement>;
        registerOnChange(syntheticEvent);
      }
    }, [e164Value, registerOnChange, name]);

    useEffect(() => {
      document.addEventListener('click', handleDocumentClick as EventListener);

      return () => {
        document.removeEventListener('click', handleDocumentClick as EventListener);
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }
      };
    }, []);

    // Scroll focused option into view
    useEffect(() => {
      if (focusedIndex >= 0 && optionRefs.current[focusedIndex]) {
        optionRefs.current[focusedIndex]?.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth',
        });
      }
    }, [focusedIndex]);

    // Reset focus when menu closes
    useEffect(() => {
      if (!isMenuOpen) {
        setFocusedIndex(-1);
        setSearchBuffer('');
      }
    }, [isMenuOpen]);

    useEffect(() => {
      // Priority: country from E.164 value -> locale -> selectedOption -> 'US'
      let countryFromValue: CountryCode | undefined;

      // Try to derive country from E.164 phone number value
      if (value && value.startsWith('+')) {
        try {
          const parsed = parsePhoneNumberWithError(value);
          // Check if country exists in COUNTRY_CODES
          if (parsed?.country && COUNTRY_CODES.some((c) => c.countryCode === parsed.country)) {
            countryFromValue = parsed.country as CountryCode;
          }
        } catch {
          // Invalid phone number, fall through to other options
        }
      }

      const countryFromLocale = getCountryCodeFromLocale(locale);
      const countryToUse = countryFromValue || countryFromLocale || selectedOption || 'US';

      const activeOption = COUNTRY_CODES.find((option) => option.countryCode === countryToUse);
      if (activeOption) {
        setOptionSelected(activeOption);
      }
    }, [locale, selectedOption, value]);

    useEffect(() => {
      if (optionSelected) {
        const exampleNumber = getExampleNumber(
          optionSelected?.countryCode as CountryCode,
          examples
        )?.nationalNumber;
        if (exampleNumber) {
          const formattedPlaceholder = parsePhoneNumberWithError(
            exampleNumber,
            optionSelected?.countryCode as CountryCode
          ).formatNational();
          setPlaceholder(formattedPlaceholder);
        }
        // Clear phone number only when country code changes (not on initial load)
        if (previousCountryCode && previousCountryCode !== optionSelected.countryCode) {
          setPhoneNumber('');
          setE164Value('');
        }
        setPreviousCountryCode(optionSelected.countryCode);
      }
    }, [optionSelected, previousCountryCode]);

    const constructOptionElement = (option: CountryOption, index: number) => {
      const isFocused = focusedIndex === index;
      const isSelected = optionSelected?.countryCode === option?.countryCode;

      return (
        <li
          key={`option_${index}`}
          ref={(el) => {
            optionRefs.current[index] = el;
          }}
          tabIndex={-1}
          className={classnames(styles.menuItem, styles[direction], {
            [styles.active]: isSelected,
            [styles.focused]: isFocused,
          })}
          role="option"
          aria-selected={isSelected}
          onClick={() => handleMenuItemClick(option)}
        >
          <Typography
            className={classnames(styles.menuItemLabel, {
              [styles.selected]: isSelected,
            })}
            variant="body"
            size={size}
            direction={direction}
          >
            {option?.label}
          </Typography>
          <Box className={styles.phoneCode} pl={4} direction={direction}>
            <Typography
              className={classnames(styles.menuItemLabel, {
                [styles.selected]: isSelected,
              })}
              variant="body"
              size={size}
              direction={direction}
            >
              ({option?.phoneCode})
            </Typography>
          </Box>
        </li>
      );
    };

    return (
      <Box
        className={classnames(
          styles.phoneInputContainer,
          styles[`theme_${theme}`],
          styles[`size_${size}`],
          {
            [styles.disabled]: disabled,
            [styles[`validation-${validationResponse?.validationType}`]]:
              validationResponse?.validationType,
          }
        )}
      >
        {(label || helperText) && (
          <Box mb={4} dir={direction}>
            {label && (
              <label htmlFor={name}>
                <Typography
                  className={styles.label}
                  variant="label"
                  size="small"
                  direction={direction}
                  theme={theme}
                  color="gray-3"
                >
                  {label}
                  {required && '*'}
                </Typography>
              </label>
            )}
            {helperText && (
              <Typography
                className={styles.helperText}
                variant="body"
                size="extraSmall"
                direction={direction}
                theme={theme}
                color="gray-5"
              >
                {helperText}
              </Typography>
            )}
          </Box>
        )}
        <Box className={classnames(styles.container, styles[direction])}>
          <Box
            ref={dropdownButtonRef}
            className={classnames(styles.dropdownMenuButton, styles[direction], {
              [styles.disabled]: disabled,
              [styles.noOutline]: inline,
            })}
            onClick={handleButtonClick}
            onKeyDown={handleKeyDown}
            aria-expanded={isMenuOpen}
            role="button"
            tabIndex={0}
            aria-haspopup="listbox"
            aria-label={placeholder}
          >
            <Typography
              className={classnames(styles.selectedOption, {
                [styles.placeholder]: !optionSelected,
                [styles.disabled]: disabled,
              })}
              variant="body"
              size={size}
              theme={theme}
              color={optionSelected ? 'white' : disabled ? 'gray-5' : 'gray-3'}
              direction={direction}
            >
              {optionSelected?.phoneCode}
            </Typography>
            <Box pl={4}>
              <Iconography
                className={classnames({
                  [styles.dropdownIcon]: isMenuOpen,
                })}
                category="arrows"
                name="chevron-down"
                theme={theme}
                color="white"
                size={{ xs: 16, md: 20, lg: 24 }}
              />
            </Box>
          </Box>
          {/* Hidden input stores E.164 value for form submission */}
          <input
            ref={(el) => {
              hiddenInputRef.current = el;
              // Also set the register ref if provided
              const registerRef = registerProps.ref as
                | React.RefCallback<HTMLInputElement>
                | undefined;
              if (registerRef) registerRef(el);
            }}
            type="hidden"
            name={name}
            value={e164Value}
            onChange={() => {}} // Handled via useEffect
          />
          {/* Visible input shows formatted display value */}
          <input
            ref={ref}
            className={classnames(
              styles.phoneInput,
              styles[size],
              styles[`direction-${direction}`]
            )}
            type="tel"
            placeholder={placeholder}
            value={phoneNumber}
            required={required}
            disabled={disabled}
            id={name}
            data-phone-input="true"
            aria-label={placeholder}
            onChange={handleChange}
            autoComplete="tel-national"
            aria-describedby={name ? `${name}-validation-message` : undefined}
          />
          <Iconography
            category="global"
            name="phone"
            color={theme === 'dark' ? 'white' : 'black'}
          />
        </Box>
        {isMenuOpen && (
          <ul ref={dropdownMenuRef} className={styles.dropdownMenu} role="listbox">
            {COUNTRY_CODES.map((option: CountryOption, index: number) =>
              constructOptionElement(option, index)
            )}
          </ul>
        )}
        {showValidation && (
          <Box className={styles.validationMessageWrapper} role="alert" aria-live="assertive">
            {validationResponse?.message && (
              <Typography
                className={styles.validationMessage}
                variant="body"
                size="extraSmall"
                direction={direction}
                theme={theme}
                id={name ? `${name}-validation-message` : undefined}
              >
                {validationResponse.message}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    );
  }
);

PhoneInput.displayName = 'PhoneInput';

export default PhoneInput;
